import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { importHRLab, getADUsers, getDebugMatch, getSyncPreview, executeSync, setAdExclusion, updateEmployeeContact, getContactsPreview, executeContactsSync, wpOfficeHealth, runWpOfficeDelta, runWpOfficeBulk, getWpOfficeStatus, linkEmployeeAd } from "../services/api";
import ProgressBar from "../components/ProgressBar";

/**
 * Spiegelt graphService.generateUsername (Backend) für die Live-Vorschau im Frontend:
 * erzeugt den lokalen Teil der UPN/E-Mail-Adresse aus Vor- und Nachname.
 */
function previewUsername(firstName, lastName) {
  const clean = (s) =>
    (s || "")
      .toLowerCase()
      .replace(/ä/g, "ae")
      .replace(/ö/g, "oe")
      .replace(/ü/g, "ue")
      .replace(/ß/g, "ss")
      .replace(/[^a-z]/g, "");
  const first = clean(firstName);
  const last = clean(lastName);
  if (!first && !last) return null;
  return `${first}.${last}`;
}

const WP_STATUS_INFO = {
  idle:      { label: "bereit", color: "#666" },
  running:   { label: "läuft", color: "#ff8c00" },
  completed: { label: "abgeschlossen", color: "#107c10" },
  error:     { label: "Fehler", color: "#d13438" },
};

/**
 * Karte zur Fernsteuerung des WordPress-Plugins "Freestyle WP Office Sync"
 * (Entra → Ultimate Member): Verbindungstest, synchroner Delta-Sync und
 * Bulk-Sync (Hintergrundlauf im Plugin) mit Status-Polling. Spricht ausschließlich
 * das FSNW-Backend an (/sync/wp-office/*); WP-Details liegen im Backend-Adapter.
 */
function WpOfficeSyncCard() {
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  const [deltaRunning, setDeltaRunning] = useState(false);
  const [deltaResult, setDeltaResult] = useState(null);

  const [resume, setResume] = useState(false);
  const [bulkStarting, setBulkStarting] = useState(false);

  const [status, setStatus] = useState(null);
  const [actionError, setActionError] = useState(null);
  const pollRef = useRef(null);

  const isRunning = status?.status === "running";

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const fetchStatus = async () => {
    try {
      const res = await getWpOfficeStatus();
      setStatus(res.data);
      return res.data;
    } catch {
      return null; // Status-Abruf ist unkritisch (z.B. noch nie gelaufen)
    }
  };

  const startPolling = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const s = await fetchStatus();
      if (!s || s.status === "completed" || s.status === "error") stopPolling();
    }, 1000);
  };

  useEffect(() => {
    fetchStatus().then((s) => { if (s && s.status === "running") startPolling(); });
    return stopPolling; // Aufräumen beim Unmount
    // eslint-disable-next-line
  }, []);

  const handleHealth = async () => {
    setHealthLoading(true);
    setHealth(null);
    try {
      const res = await wpOfficeHealth();
      setHealth({ type: "success", data: res.data });
    } catch (err) {
      setHealth({ type: "error", message: err.response?.data?.error || err.message });
    } finally {
      setHealthLoading(false);
    }
  };

  const handleDelta = async () => {
    setDeltaRunning(true);
    setDeltaResult(null);
    setActionError(null);
    try {
      const res = await runWpOfficeDelta();
      setDeltaResult(res.data);
      fetchStatus();
    } catch (err) {
      setActionError(err.response?.data?.error || err.message);
    } finally {
      setDeltaRunning(false);
    }
  };

  const handleBulk = async () => {
    if (!window.confirm(
      `WP-Office Bulk-Sync ${resume ? "fortsetzen" : "starten"}?\n\n` +
      `Synchronisiert die Entra-Nutzer ins WordPress (Ultimate Member). ` +
      `Läuft im Hintergrund des Plugins – der Fortschritt erscheint hier.`
    )) return;
    setBulkStarting(true);
    setActionError(null);
    try {
      await runWpOfficeBulk(resume);
      await fetchStatus();
      startPolling();
    } catch (err) {
      setActionError(err.response?.data?.error || err.message);
    } finally {
      setBulkStarting(false);
    }
  };

  const statusInfo = WP_STATUS_INFO[status?.status] || WP_STATUS_INFO.idle;
  const hrStyle = { border: "none", borderTop: "1px solid #f0f0f0", margin: "16px 0" };

  return (
    <div className="card">
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <div style={{
          background: "#5c2d9110", border: "2px solid #5c2d91", borderRadius: 8,
          width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 700, color: "#5c2d91", fontSize: 13, flexShrink: 0,
        }}>WP</div>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>WP Office Sync (WordPress / Ultimate Member)</h2>
          <p style={{ fontSize: 13, color: "#666", margin: 0 }}>
            Stößt im WordPress-Plugin „Freestyle WP Office Sync" den Abgleich
            Entra → Ultimate Member an: Delta sofort, Bulk als Hintergrundlauf.
          </p>
        </div>
      </div>

      {actionError && (
        <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 14, background: "#fde7e9", color: "#d13438", fontSize: 14 }}>
          ❌ {actionError}
        </div>
      )}

      {/* Verbindung */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <button className="btn-secondary" onClick={handleHealth} disabled={healthLoading}>
          {healthLoading ? "⏳ Prüft..." : "🔌 Verbindung testen"}
        </button>
        {health && (
          <span style={{ fontSize: 13, color: health.type === "success" ? "#107c10" : "#d13438" }}>
            {health.type === "success"
              ? `✅ Verbunden · Plugin ${health.data.plugin_version || "?"} · Graph ${health.data.graph_ok ? "ok" : "Fehler"}` +
                (health.data.remote_enabled === false ? " · ⚠️ Fernsteuerung deaktiviert" : "")
              : `❌ ${health.message}`}
          </span>
        )}
      </div>

      <hr style={hrStyle} />

      {/* Delta */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <button className="btn-secondary" onClick={handleDelta} disabled={deltaRunning}>
          {deltaRunning ? "⏳ Läuft..." : "🔄 Delta-Sync jetzt"}
        </button>
        <span style={{ fontSize: 12, color: "#999" }}>Synchron, wenige geänderte Nutzer pro Lauf.</span>
      </div>
      {deltaRunning && <ProgressBar label="Delta-Sync läuft im Plugin…" />}
      {deltaResult && (
        <div style={{
          marginTop: 8, padding: "10px 14px", borderRadius: 8, fontSize: 13,
          background: deltaResult.status === "error" ? "#fde7e9" : "#dff6dd",
          color: deltaResult.status === "error" ? "#d13438" : "#107c10",
        }}>
          {deltaResult.status === "error" ? "⚠️" : "✅"} Delta: {deltaResult.processed ?? 0} verarbeitet
          {" "}(➕/✏️ {deltaResult.upserted ?? 0}, 🚫 {deltaResult.deactivated ?? 0}, übersprungen {deltaResult.skipped ?? 0})
          {deltaResult.error_count > 0 && <> · {deltaResult.error_count} Fehler</>}
        </div>
      )}

      <hr style={hrStyle} />

      {/* Bulk */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <button
          onClick={handleBulk}
          disabled={bulkStarting || isRunning}
          style={{
            padding: "9px 18px", fontSize: 14, fontWeight: 600, borderRadius: 8,
            background: (bulkStarting || isRunning) ? "#ccc" : "#5c2d91",
            color: "white", border: "none", cursor: (bulkStarting || isRunning) ? "default" : "pointer",
          }}
        >
          {bulkStarting ? "⏳ Wird gestartet..." : isRunning ? "⏳ Läuft bereits…" : "📦 Bulk-Sync starten"}
        </button>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: isRunning ? "default" : "pointer" }}>
          <input type="checkbox" checked={resume} disabled={isRunning} onChange={(e) => setResume(e.target.checked)} />
          Unterbrochenen Lauf fortsetzen
        </label>
        <button className="btn-secondary" onClick={fetchStatus} style={{ marginLeft: "auto" }}>↻ Status</button>
      </div>

      {status && status.status !== "idle" && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, marginBottom: 6, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 600, color: statusInfo.color }}>{statusInfo.label}</span>
            <span>{status.processed ?? 0}{status.total != null ? ` / ${status.total}` : ""} verarbeitet</span>
            {status.error_count > 0 && <span style={{ color: "#d13438" }}>· {status.error_count} Fehler</span>}
            {status.last_batch_at && (
              <span style={{ color: "#999" }}>· zuletzt {new Date(status.last_batch_at).toLocaleTimeString("de-DE")}</span>
            )}
          </div>
          {isRunning && <ProgressBar label="Bulk-Sync läuft im Plugin…" />}
          {status.errors?.length > 0 && (
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: "pointer", fontSize: 12, color: "#d13438" }}>
                {status.error_count} Fehler anzeigen
              </summary>
              <ul style={{ fontSize: 12, color: "#666", margin: "6px 0 0", paddingLeft: 18 }}>
                {status.errors.slice(0, 20).map((e, i) => (
                  <li key={i}><code>{e.entraId || "—"}</code> – {e.message}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

export default function SyncPage() {
  const [hrImporting, setHrImporting] = useState(false);
  const [hrResult, setHrResult] = useState(null);

  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selectedCreate, setSelectedCreate] = useState(new Set());
  const [selectedDeactivate, setSelectedDeactivate] = useState(new Set());

  const [executing, setExecuting] = useState(false);
  const [executeResult, setExecuteResult] = useState(null);

  const [adUsers, setAdUsers] = useState(null);
  const [adUsersLoading, setAdUsersLoading] = useState(false);
  const [adUsersError, setAdUsersError] = useState(null);

  const [matchResult, setMatchResult] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchError, setMatchError] = useState(null);

  // Schritt 3: Mitarbeiter-Kontaktverteilung
  const [contactsPreviewLoading, setContactsPreviewLoading] = useState(false);
  const [contactsPreviewError, setContactsPreviewError] = useState(null);
  const [contactsPreview, setContactsPreview] = useState(null);
  const [contactsExecuting, setContactsExecuting] = useState(false);
  const [contactsExecuteResult, setContactsExecuteResult] = useState(null);

  // Detail-Vorschau pro Mitarbeiter (Schritt 2)
  const [expanded, setExpanded] = useState(new Set());
  const [phoneEdits, setPhoneEdits] = useState({});       // hrlab_id -> string
  const [phoneSaving, setPhoneSaving] = useState({});     // hrlab_id -> bool
  const [phoneSaveMsg, setPhoneSaveMsg] = useState({});   // hrlab_id -> message
  const [extraLicenses, setExtraLicenses] = useState({}); // hrlab_id -> Set(skuId)
  const [nameEdits, setNameEdits] = useState({});         // hrlab_id -> { firstName, lastName }

  const handleLoadADUsers = async () => {
    setAdUsersLoading(true);
    setAdUsersError(null);
    try {
      const res = await getADUsers();
      setAdUsers(res.data);
    } catch (err) {
      setAdUsersError(err.response?.data?.error || err.message);
    } finally {
      setAdUsersLoading(false);
    }
  };

  const handleDebugMatch = async () => {
    setMatchLoading(true);
    setMatchError(null);
    try {
      const res = await getDebugMatch();
      setMatchResult(res.data);
    } catch (err) {
      setMatchError(err.response?.data?.error || err.message);
    } finally {
      setMatchLoading(false);
    }
  };

  const handleHRImport = async () => {
    setHrImporting(true);
    setHrResult(null);
    try {
      const res = await importHRLab();
      setHrResult({
        type: "success",
        message: `✅ ${res.data.imported} Mitarbeiter importiert${res.data.skipped ? ` (${res.data.skipped} ohne E-Mail übersprungen)` : ""}`,
      });
    } catch (err) {
      setHrResult({ type: "error", message: "❌ " + (err.response?.data?.error || err.message) });
    } finally {
      setHrImporting(false);
    }
  };

  const handleLoadPreview = async () => {
    setPreviewLoading(true);
    setPreviewError(null);
    setExecuteResult(null);
    try {
      const res = await getSyncPreview();
      setPreview(res.data);
      // Bewusst NICHTS vorauswählen – der Admin wählt aktiv an, was ausgeführt wird.
      setSelectedCreate(new Set());
      setSelectedDeactivate(new Set());
    } catch (err) {
      setPreviewError(err.response?.data?.error || err.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const toggleSelected = (set, setSet, id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSet(next);
  };

  const toggleExpanded = (hrlabId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(hrlabId)) next.delete(hrlabId); else next.add(hrlabId);
      return next;
    });
  };

  const toggleExtraLicense = (hrlabId, skuId) => {
    setExtraLicenses((prev) => {
      const current = new Set(prev[hrlabId] || []);
      if (current.has(skuId)) current.delete(skuId); else current.add(skuId);
      return { ...prev, [hrlabId]: current };
    });
  };

  const handleSavePhone = async (emp) => {
    const value = (phoneEdits[emp.hrlab_id] ?? "").trim();
    if (!value) return;
    setPhoneSaving((p) => ({ ...p, [emp.hrlab_id]: true }));
    setPhoneSaveMsg((p) => ({ ...p, [emp.hrlab_id]: null }));
    try {
      await updateEmployeeContact(emp.hrlab_id, { phone: value });
      setPreview((p) => ({
        ...p,
        toCreate: p.toCreate.map((e) =>
          e.hrlab_id === emp.hrlab_id ? { ...e, phone: value, phone_source: "hrlab" } : e
        ),
      }));
      setPhoneSaveMsg((p) => ({ ...p, [emp.hrlab_id]: { type: "success", text: "✅ In HRLab gespeichert" } }));
    } catch (err) {
      setPhoneSaveMsg((p) => ({ ...p, [emp.hrlab_id]: { type: "error", text: "❌ " + (err.response?.data?.error || err.message) } }));
    } finally {
      setPhoneSaving((p) => ({ ...p, [emp.hrlab_id]: false }));
    }
  };

  // Manuelle AD-Verknüpfung: hrlab_id des Mitarbeiters, für den der
  // Auswahl-Dialog geöffnet ist, plus Suchtext und Lade-/Fehlerzustand.
  const [linkingFor, setLinkingFor] = useState(null);
  const [linkSearch, setLinkSearch] = useState("");
  const [linkSaving, setLinkSaving] = useState(false);
  const [linkError, setLinkError] = useState(null);
  const [linkAdUsers, setLinkAdUsers] = useState(null);
  const [linkAdUsersLoading, setLinkAdUsersLoading] = useState(false);

  const handleOpenLink = async (emp) => {
    if (linkingFor === emp.hrlab_id) { setLinkingFor(null); return; }
    setLinkingFor(emp.hrlab_id);
    setLinkSearch(emp.last_name || "");
    setLinkError(null);
    if (!linkAdUsers) {
      setLinkAdUsersLoading(true);
      try {
        const res = await getADUsers();
        setLinkAdUsers(res.data.users || []);
      } catch (err) {
        setLinkError(err.response?.data?.error || err.message);
      } finally {
        setLinkAdUsersLoading(false);
      }
    }
  };

  const handleLink = async (emp, adUser) => {
    if (!window.confirm(
      `Verknüpfen?\n\n` +
      `HRLab: ${emp.name} (${emp.email})\n` +
      `Azure AD: ${adUser.displayName} (${adUser.userPrincipalName})\n\n` +
      `Die HRLab-ID wird dauerhaft im AD-Konto hinterlegt – der Sync behandelt beide ab sofort als dieselbe Person.`
    )) return;
    setLinkSaving(true);
    setLinkError(null);
    try {
      await linkEmployeeAd(emp.hrlab_id, adUser.id);
      // Eintrag aus der Anlage-Liste entfernen – Konto existiert ja bereits
      setPreview((p) => ({
        ...p,
        toCreate: p.toCreate.filter((e) => e.hrlab_id !== emp.hrlab_id),
        inSync: p.toCreate.length <= 1 && p.toDeactivate.length === 0,
      }));
      setSelectedCreate((s) => {
        const next = new Set(s);
        next.delete(emp.hrlab_id);
        return next;
      });
      setLinkingFor(null);
    } catch (err) {
      setLinkError(err.response?.data?.error || err.message);
    } finally {
      setLinkSaving(false);
    }
  };

  const [excluding, setExcluding] = useState(null);
  const handleExcludeFromCreate = async (emp) => {
    setExcluding(emp.hrlab_id);
    try {
      await setAdExclusion(emp.hrlab_id, { excluded: true, reason: "" });
      setPreview((p) => ({
        ...p,
        toCreate: p.toCreate.filter((e) => e.hrlab_id !== emp.hrlab_id),
        excluded: [...(p.excluded || []), { ...emp, reason: null }],
        inSync: p.toCreate.length <= 1 && p.toDeactivate.length === 0,
      }));
      setSelectedCreate((s) => {
        const next = new Set(s);
        next.delete(emp.hrlab_id);
        return next;
      });
    } catch (err) {
      setPreviewError(err.response?.data?.error || err.message);
    } finally {
      setExcluding(null);
    }
  };

  const handleExecute = async () => {
    const create = [...selectedCreate];
    const deactivate = [...selectedDeactivate];
    if (create.length === 0 && deactivate.length === 0) return;

    if (!window.confirm(
      `Ausgewählte Aktionen ausführen?\n\n` +
      `➕ ${create.length} Nutzer in Azure AD anlegen\n` +
      `🚫 ${deactivate.length} Nutzer in Azure AD deaktivieren\n\n` +
      `⚠️ Diese Aktionen können nicht rückgängig gemacht werden!`
    )) return;

    setExecuting(true);
    setExecuteResult(null);
    try {
      const extraLicensesPayload = {};
      for (const id of create) {
        const set = extraLicenses[id];
        if (set && set.size > 0) extraLicensesPayload[id] = [...set];
      }
      const nameOverridesPayload = {};
      for (const id of create) {
        const edit = nameEdits[id];
        if (!edit) continue;
        const emp = preview?.toCreate?.find((x) => x.hrlab_id === id);
        const firstName = (edit.firstName ?? "").trim();
        const lastName = (edit.lastName ?? "").trim();
        if (!firstName && !lastName) continue;
        if (emp && firstName === (emp.first_name || "") && lastName === (emp.last_name || "")) continue;
        nameOverridesPayload[id] = { first_name: firstName, last_name: lastName };
      }
      const res = await executeSync({ create, deactivate, extraLicenses: extraLicensesPayload, nameOverrides: nameOverridesPayload });
      setExecuteResult({ type: "success", data: res.data });
      // Vorschau neu laden, da Aktionen ausgeführt wurden
      await handleLoadPreview();
    } catch (err) {
      setExecuteResult({ type: "error", message: err.response?.data?.error || err.message });
    } finally {
      setExecuting(false);
    }
  };

  const handleLoadContactsPreview = async () => {
    setContactsPreviewLoading(true);
    setContactsPreviewError(null);
    setContactsExecuteResult(null);
    try {
      const res = await getContactsPreview();
      setContactsPreview(res.data);
    } catch (err) {
      setContactsPreviewError(err.response?.data?.error || err.message);
    } finally {
      setContactsPreviewLoading(false);
    }
  };

  const handleExecuteContacts = async () => {
    if (!window.confirm(
      `Kontaktverteilung jetzt ausführen?\n\n` +
      `➕ ${contactsPreview?.totals?.create || 0} Kontakte anlegen\n` +
      `✏️ ${contactsPreview?.totals?.update || 0} Kontakte aktualisieren\n` +
      `🗑️ ${contactsPreview?.totals?.delete || 0} Kontakte entfernen\n\n` +
      `Dies läuft im Hintergrund und kann je nach Mitarbeiterzahl mehrere Minuten dauern. ` +
      `Den Fortschritt siehst du auf der Logs-Seite.`
    )) return;

    setContactsExecuting(true);
    setContactsExecuteResult(null);
    try {
      const res = await executeContactsSync();
      setContactsExecuteResult({ type: "success", data: res.data });
    } catch (err) {
      setContactsExecuteResult({ type: "error", message: err.response?.data?.error || err.message });
    } finally {
      setContactsExecuting(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700 }}>Synchronisation</h1>
        <p style={{ color: "#666", marginTop: 4 }}>HRLab → Lokale Datenbank → Azure AD</p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

        {/* Schritt 1: HRLab importieren */}
        <div className="card">
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div style={{
              background: "#0078d410", border: "2px solid #0078d4", borderRadius: 8,
              width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 700, color: "#0078d4", fontSize: 16, flexShrink: 0,
            }}>1</div>
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>HRLab-Daten importieren</h2>
              <p style={{ fontSize: 13, color: "#666", margin: 0 }}>
                Liest alle Mitarbeiter aus HRLab und speichert sie lokal. Kein AD-Zugriff.
                Die Sync-Vorschau (Schritt 2) nutzt diesen lokalen Stand – führe diesen Schritt
                also einmal aus (und danach nur, wenn sich Daten in HRLab geändert haben).
              </p>
            </div>
          </div>

          {hrResult && (
            <div style={{
              padding: "10px 14px", borderRadius: 8, marginBottom: 14,
              background: hrResult.type === "success" ? "#dff6dd" : "#fde7e9",
              color: hrResult.type === "success" ? "#107c10" : "#d13438",
              fontSize: 14,
            }}>
              {hrResult.message}
            </div>
          )}

          <button
            className="btn-primary"
            onClick={handleHRImport}
            disabled={hrImporting}
            style={{ padding: "11px 24px", fontSize: 14, fontWeight: 600 }}
          >
            {hrImporting ? "⏳ Lädt HRLab-Daten..." : "📥 HRLab-Daten jetzt laden"}
          </button>

          {hrImporting && <ProgressBar label="HRLab-Daten werden importiert…" />}

          <p style={{ marginTop: 10, fontSize: 12, color: "#999" }}>
            Sicher auszuführen – erstellt oder ändert keine Konten.
          </p>
        </div>

        {/* Schritt 2: Azure AD Sync – Vorschau & Bestätigung */}
        <div className="card">
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div style={{
              background: "#d1343810", border: "2px solid #d13438", borderRadius: 8,
              width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 700, color: "#d13438", fontSize: 16, flexShrink: 0,
            }}>2</div>
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Azure AD Sync</h2>
              <p style={{ fontSize: 13, color: "#666", margin: 0 }}>
                Vergleicht den lokalen Stand (Schritt 1) live mit Azure AD – ohne erneut HRLab abzufragen.
                Es wird nichts in Azure AD geändert, bis du die ausgewählten Aktionen ausdrücklich ausführst.
              </p>
            </div>
          </div>

          {previewError && (
            <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 14, background: "#fde7e9", color: "#d13438", fontSize: 14 }}>
              ❌ {previewError}
            </div>
          )}

          {executeResult && (
            <div style={{
              padding: "10px 14px", borderRadius: 8, marginBottom: 14,
              background: executeResult.type === "success" ? "#dff6dd" : "#fde7e9",
              color: executeResult.type === "success" ? "#107c10" : "#d13438",
              fontSize: 14,
            }}>
              {executeResult.type === "success" ? (
                <>
                  ✅ Erledigt: {executeResult.data.summary.created_ad} angelegt,{" "}
                  {executeResult.data.summary.deactivated_ad} deaktiviert
                  {executeResult.data.summary.errors?.length > 0 && (
                    <> – {executeResult.data.summary.errors.length} Fehler (siehe Logs)</>
                  )}
                </>
              ) : (
                <>❌ {executeResult.message}</>
              )}
            </div>
          )}

          <button
            className="btn-secondary"
            onClick={handleLoadPreview}
            disabled={previewLoading}
            style={{ marginBottom: 16 }}
          >
            {previewLoading ? "⏳ Lädt Vorschau..." : "🔍 Sync-Vorschau laden"}
          </button>

          {previewLoading && <ProgressBar label="Vorschau wird geladen – HRLab-Stand wird mit Azure AD verglichen…" />}

          {preview?.no_cache && (
            <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 14, background: "#fff4ce", color: "#ad7800", fontSize: 14 }}>
              ⚠️ Noch keine HRLab-Daten lokal vorhanden – bitte zuerst Schritt 1 ausführen.
            </div>
          )}

          {preview && !preview.no_cache && (
            <>
              {preview.inSync ? (
                <div style={{ padding: "12px 16px", borderRadius: 8, background: "#dff6dd", color: "#107c10", fontSize: 14, fontWeight: 600 }}>
                  ✅ Alles synchron – keine Aktionen nötig.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {preview.toCreate.length > 0 && (
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "0 0 8px" }}>
                        <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>
                          ➕ In Azure AD anlegen ({preview.toCreate.length})
                        </h3>
                        <button
                          onClick={() => setSelectedCreate(new Set(preview.toCreate.map((e) => e.hrlab_id)))}
                          style={{ fontSize: 11, padding: "2px 8px", borderRadius: 6, border: "1px solid #e1e4e8", background: "#fff", color: "#0078d4", cursor: "pointer" }}
                        >
                          Alle auswählen
                        </button>
                        <button
                          onClick={() => setSelectedCreate(new Set())}
                          disabled={selectedCreate.size === 0}
                          style={{ fontSize: 11, padding: "2px 8px", borderRadius: 6, border: "1px solid #e1e4e8", background: "#fff", color: "#666", cursor: "pointer" }}
                        >
                          Auswahl aufheben
                        </button>
                      </div>
                      <div style={{ maxHeight: 480, overflow: "auto", border: "1px solid #e1e4e8", borderRadius: 8 }}>
                        {preview.toCreate.map((e) => {
                          const isOpen = expanded.has(e.hrlab_id);
                          const extraSet = extraLicenses[e.hrlab_id] || new Set();
                          const defaultSkuIds = new Set((e.default_licenses || []).map((l) => l.skuId));
                          const additionalSkus = (preview.available_licenses || []).filter((s) => !defaultSkuIds.has(s.skuId));
                          const phoneVal = phoneEdits[e.hrlab_id] ?? (e.phone || "");
                          const saveMsg = phoneSaveMsg[e.hrlab_id];

                          const nameEdit = nameEdits[e.hrlab_id];
                          const firstNameVal = nameEdit?.firstName ?? e.first_name ?? "";
                          const lastNameVal = nameEdit?.lastName ?? e.last_name ?? "";
                          const upnDomain = (e.generated_upn || "").split("@")[1] || "";
                          const previewLocalPart = previewUsername(firstNameVal, lastNameVal);
                          const previewUpn = previewLocalPart ? `${previewLocalPart}@${upnDomain}` : e.generated_upn;

                          return (
                            <div key={e.hrlab_id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                              <div style={{
                                display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                                fontSize: 13,
                              }}>
                                <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", flex: 1 }}>
                                  <input
                                    type="checkbox"
                                    checked={selectedCreate.has(e.hrlab_id)}
                                    onChange={() => toggleSelected(selectedCreate, setSelectedCreate, e.hrlab_id)}
                                  />
                                  <span style={{ fontWeight: 500 }}>{e.name}</span>
                                  <span style={{ color: "#888" }}>{e.email}</span>
                                  {e.starts_in_future && (
                                    <span
                                      title="Erster Arbeitstag liegt in der Zukunft"
                                      style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, background: "#fff4ce", color: "#ad7800", whiteSpace: "nowrap" }}
                                    >
                                      ⏳ startet {new Date(e.start_date).toLocaleDateString("de-DE")}
                                    </span>
                                  )}
                                  {e.email_is_external && (
                                    <span
                                      title="Keine der HRLab-E-Mails liegt auf der Firmen-Domain – vermutlich steht in HRLab noch die private Adresse als geschäftlich drin. Falls das AD-Konto schon existiert: verknüpfen statt anlegen."
                                      style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, background: "#fde7e9", color: "#d13438", whiteSpace: "nowrap" }}
                                    >
                                      ⚠️ private E-Mail?
                                    </span>
                                  )}
                                  {e.possible_duplicate && (
                                    <span
                                      title={`Es existiert bereits ein AD-Konto mit demselben Namen: ${e.possible_duplicate.name} (${e.possible_duplicate.upn}). Vermutlich dieselbe Person – wahrscheinlich stimmt die HRLab-E-Mail nicht mit dem AD-Konto überein (z.B. Tippfehler).`}
                                      style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, background: "#fde7e9", color: "#d13438", whiteSpace: "nowrap", fontWeight: 600 }}
                                    >
                                      ⚠️ evtl. Duplikat!
                                    </span>
                                  )}
                                </label>
                                <button
                                  onClick={() => handleOpenLink(e)}
                                  title="Mit einem bestehenden Azure-AD-Konto verknüpfen (gleiche Person, z.B. nach Namensänderung oder vorab angelegtem Konto)"
                                  style={{
                                    fontSize: 12, padding: "4px 10px", borderRadius: 6,
                                    border: "1px solid #e1e4e8",
                                    background: linkingFor === e.hrlab_id ? "#0078d410" : "#fff",
                                    color: "#0078d4", cursor: "pointer", whiteSpace: "nowrap",
                                  }}
                                >
                                  🔗 Verknüpfen
                                </button>
                                <button
                                  onClick={() => toggleExpanded(e.hrlab_id)}
                                  style={{
                                    fontSize: 12, padding: "4px 10px", borderRadius: 6,
                                    border: "1px solid #e1e4e8", background: "#fff", color: "#0078d4", cursor: "pointer",
                                  }}
                                >
                                  {isOpen ? "▲ Details ausblenden" : "▼ Details / Vorschau"}
                                </button>
                                <button
                                  onClick={() => handleExcludeFromCreate(e)}
                                  disabled={excluding === e.hrlab_id}
                                  title="Diesen Mitarbeiter dauerhaft ignorieren (kein AD-Konto nötig)"
                                  style={{
                                    fontSize: 12, padding: "4px 10px", borderRadius: 6,
                                    border: "1px solid #e1e4e8", background: "#f8f9fa", color: "#666", cursor: "pointer",
                                  }}
                                >
                                  {excluding === e.hrlab_id ? "…" : "🚫 Ignorieren"}
                                </button>
                              </div>

                              {e.possible_duplicate && (
                                <div style={{
                                  display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                                  padding: "8px 16px 8px 38px", background: "#fde7e9", fontSize: 12, color: "#8a1f22",
                                }}>
                                  <span>
                                    ⚠️ In Azure AD existiert bereits <strong>{e.possible_duplicate.name}</strong>{" "}
                                    ({e.possible_duplicate.upn || e.possible_duplicate.mail})
                                    {!e.possible_duplicate.enabled && " – deaktiviert"} – vermutlich dieselbe Person.
                                  </span>
                                  <button
                                    onClick={() => handleLink(e, {
                                      id: e.possible_duplicate.ad_user_id,
                                      displayName: e.possible_duplicate.name,
                                      userPrincipalName: e.possible_duplicate.upn,
                                    })}
                                    disabled={linkSaving}
                                    style={{
                                      fontSize: 12, padding: "4px 10px", borderRadius: 6,
                                      border: "1px solid #8a1f22", background: "#fff", color: "#8a1f22", cursor: "pointer", whiteSpace: "nowrap",
                                    }}
                                  >
                                    🔗 Statt anlegen verknüpfen
                                  </button>
                                </div>
                              )}

                              {linkingFor === e.hrlab_id && (
                                <div style={{ padding: "8px 16px 14px 38px", background: "#f8fbff", borderTop: "1px dashed #cfe4f7" }}>
                                  <div style={{ fontSize: 12, color: "#555", marginBottom: 6 }}>
                                    Bestehendes Azure-AD-Konto auswählen, das zu <strong>{e.name}</strong> gehört:
                                  </div>
                                  <input
                                    type="text"
                                    placeholder="AD-Konto suchen (Name oder E-Mail)…"
                                    value={linkSearch}
                                    onChange={(ev) => setLinkSearch(ev.target.value)}
                                    style={{ width: "100%", maxWidth: 360, padding: "5px 8px", borderRadius: 6, border: "1px solid #e1e4e8", fontSize: 13, marginBottom: 6 }}
                                  />
                                  {linkAdUsersLoading && <div style={{ fontSize: 12, color: "#999" }}>⏳ AD-Konten werden geladen…</div>}
                                  {linkError && <div style={{ fontSize: 12, color: "#d13438", marginBottom: 6 }}>❌ {linkError}</div>}
                                  {linkAdUsers && (() => {
                                    const q = linkSearch.trim().toLowerCase();
                                    const matches = linkAdUsers.filter((u) =>
                                      !q ||
                                      (u.displayName || "").toLowerCase().includes(q) ||
                                      (u.mail || "").toLowerCase().includes(q) ||
                                      (u.userPrincipalName || "").toLowerCase().includes(q)
                                    );
                                    return (
                                      <div style={{ maxHeight: 220, overflow: "auto", border: "1px solid #e1e4e8", borderRadius: 6, background: "#fff" }}>
                                        {matches.length === 0 && (
                                          <div style={{ padding: "8px 12px", fontSize: 12, color: "#999" }}>Keine Treffer.</div>
                                        )}
                                        {matches.slice(0, 30).map((u) => (
                                          <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 12px", borderBottom: "1px solid #f0f0f0", fontSize: 13 }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                              <div style={{ fontWeight: 500 }}>
                                                {u.displayName}
                                                {!u.accountEnabled && <span style={{ color: "#d13438", fontSize: 11 }}> (deaktiviert)</span>}
                                              </div>
                                              <div style={{ color: "#888", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis" }}>
                                                {u.mail || u.userPrincipalName}
                                              </div>
                                            </div>
                                            <button
                                              onClick={() => handleLink(e, u)}
                                              disabled={linkSaving}
                                              style={{
                                                fontSize: 12, padding: "4px 10px", borderRadius: 6,
                                                border: "1px solid #0078d4", background: "#0078d410", color: "#0078d4", cursor: "pointer", whiteSpace: "nowrap",
                                              }}
                                            >
                                              {linkSaving ? "…" : "🔗 Verknüpfen"}
                                            </button>
                                          </div>
                                        ))}
                                        {matches.length > 30 && (
                                          <div style={{ padding: "6px 12px", fontSize: 11, color: "#999" }}>
                                            {matches.length - 30} weitere Treffer – Suche verfeinern.
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              )}

                              {isOpen && (
                                <div style={{ padding: "4px 16px 16px 38px", fontSize: 13, color: "#444", display: "flex", flexDirection: "column", gap: 12 }}>
                                  {/* Stammdaten */}
                                  <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", rowGap: 4, columnGap: 8 }}>
                                    <div style={{ color: "#999" }}>Abteilung</div>
                                    <div>{e.department || <span style={{ color: "#bbb" }}>– keine –</span>}</div>
                                    <div style={{ color: "#999" }}>Position</div>
                                    <div>{e.position || <span style={{ color: "#bbb" }}>– keine –</span>}</div>
                                  </div>

                                  {/* Name für AD-Konto (Vorschlag, editierbar) */}
                                  <div>
                                    <div style={{ color: "#999", marginBottom: 4 }}>Name für Azure-AD-Konto</div>
                                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                      <input
                                        type="text"
                                        placeholder="Vorname"
                                        value={firstNameVal}
                                        onChange={(ev) => setNameEdits((p) => ({
                                          ...p,
                                          [e.hrlab_id]: { firstName: ev.target.value, lastName: p[e.hrlab_id]?.lastName ?? e.last_name ?? "" },
                                        }))}
                                        style={{ flex: 1, minWidth: 120, padding: "5px 8px", borderRadius: 6, border: "1px solid #e1e4e8", fontSize: 13 }}
                                      />
                                      <input
                                        type="text"
                                        placeholder="Nachname"
                                        value={lastNameVal}
                                        onChange={(ev) => setNameEdits((p) => ({
                                          ...p,
                                          [e.hrlab_id]: { firstName: p[e.hrlab_id]?.firstName ?? e.first_name ?? "", lastName: ev.target.value },
                                        }))}
                                        style={{ flex: 1, minWidth: 120, padding: "5px 8px", borderRadius: 6, border: "1px solid #e1e4e8", fontSize: 13 }}
                                      />
                                    </div>
                                    <div style={{ fontSize: 12, color: "#999", marginTop: 6 }}>
                                      Vorschlag aus HRLab – bei Bedarf hier anpassen (z.B. Rufname statt
                                      vollem Vornamen). Anzeigename und E-Mail/UPN des neuen AD-Kontos werden
                                      daraus generiert:
                                    </div>
                                    <div style={{ marginTop: 4 }}>
                                      <strong>{firstNameVal} {lastNameVal}</strong>{" "}
                                      · <code>{previewUpn}</code>
                                    </div>
                                  </div>

                                  {/* Telefon */}
                                  <div>
                                    <div style={{ color: "#999", marginBottom: 4 }}>Telefon (geschäftlich)</div>
                                    {e.phone ? (
                                      <div>
                                        {e.phone}{" "}
                                        <span style={{ fontSize: 11, color: "#999" }}>
                                          ({e.phone_source === "standort_vorlage" ? "aus Standort-Vorlage" : "aus HRLab"})
                                        </span>
                                      </div>
                                    ) : (
                                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <span style={{ color: "#999", fontSize: 12 }}>— keine Festnetznummer</span>
                                      </div>
                                    )}
                                    {e.mobile && (
                                      <div style={{ marginTop: 4 }}>
                                        <span style={{ color: "#999", fontSize: 12 }}>Mobilfunk (geschäftlich): </span>
                                        <strong style={{ fontSize: 13 }}>{e.mobile}</strong>
                                      </div>
                                    )}
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                                      <input
                                        type="text"
                                        placeholder="Telefonnummer eintragen…"
                                        value={phoneVal}
                                        onChange={(ev) => setPhoneEdits((p) => ({ ...p, [e.hrlab_id]: ev.target.value }))}
                                        style={{ flex: 1, maxWidth: 220, padding: "5px 8px", borderRadius: 6, border: "1px solid #e1e4e8", fontSize: 13 }}
                                      />
                                      <button
                                        onClick={() => handleSavePhone(e)}
                                        disabled={phoneSaving[e.hrlab_id] || !phoneVal.trim()}
                                        style={{
                                          fontSize: 12, padding: "5px 10px", borderRadius: 6,
                                          border: "1px solid #0078d4", background: "#0078d410", color: "#0078d4", cursor: "pointer",
                                        }}
                                      >
                                        {phoneSaving[e.hrlab_id] ? "⏳ Sendet…" : "📤 An HRLab senden"}
                                      </button>
                                      {saveMsg && (
                                        <span style={{ fontSize: 12, color: saveMsg.type === "success" ? "#107c10" : "#d13438" }}>
                                          {saveMsg.text}
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Profil-/Standortvorlage */}
                                  <div>
                                    <div style={{ color: "#999", marginBottom: 4 }}>AD-Profilinformationen (aus Standort-Vorlage)</div>
                                    {e.template ? (
                                      <div style={{ fontSize: 12, color: "#555" }}>
                                        {e.template.office_location && <>📍 {e.template.office_location} · </>}
                                        {[e.template.street_address, e.template.postal_code, e.template.city].filter(Boolean).join(", ")}
                                        {e.template.country && <>, {e.template.country}</>}
                                        {e.template.fax && <> · Fax: {e.template.fax}</>}
                                      </div>
                                    ) : (
                                      <div style={{ fontSize: 12, color: "#d13438" }}>
                                        ⚠️ Keine Standort-Vorlage für Abteilung „{e.department || "–"}“ hinterlegt –
                                        Profilfelder (Büro, Adresse, etc.) bleiben leer.{" "}
                                        <Link to="/settings" style={{ color: "#0078d4" }}>Jetzt anlegen</Link>
                                      </div>
                                    )}
                                  </div>

                                  {/* Lizenzen */}
                                  <div>
                                    <div style={{ color: "#999", marginBottom: 4 }}>Lizenzen</div>
                                    {e.default_licenses?.length > 0 ? (
                                      <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 8 }}>
                                        {e.default_licenses.map((l) => (
                                          <div key={l.skuId} style={{ fontSize: 12 }}>
                                            ✅ {l.friendlyName} <span style={{ color: "#999" }}>(Standard)</span>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div style={{ fontSize: 12, color: "#999", marginBottom: 8 }}>
                                        Keine Standard-Lizenz konfiguriert (siehe Einstellungen).
                                      </div>
                                    )}

                                    {preview.license_error && (
                                      <div style={{ fontSize: 12, color: "#d13438" }}>
                                        ❌ Verfügbare Lizenzen konnten nicht geladen werden: {preview.license_error}
                                      </div>
                                    )}

                                    {!preview.license_error && additionalSkus.length > 0 && (
                                      <>
                                        <div style={{ fontSize: 12, color: "#999", marginBottom: 4 }}>Weitere verfügbare Lizenzen buchen:</div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                          {additionalSkus.map((s) => (
                                            <label key={s.skuId} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, cursor: "pointer" }}>
                                              <input
                                                type="checkbox"
                                                checked={extraSet.has(s.skuId)}
                                                onChange={() => toggleExtraLicense(e.hrlab_id, s.skuId)}
                                                disabled={s.available <= 0}
                                              />
                                              <span>{s.friendlyName}</span>
                                              <span style={{ color: s.available > 0 ? "#107c10" : "#d13438" }}>
                                                ({s.available} / {s.enabled} frei)
                                              </span>
                                            </label>
                                          ))}
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {preview.toDeactivate.length > 0 && (
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "0 0 8px" }}>
                        <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>
                          🚫 In Azure AD deaktivieren ({preview.toDeactivate.length})
                        </h3>
                        <button
                          onClick={() => setSelectedDeactivate(new Set(preview.toDeactivate.map((e) => e.hrlab_id)))}
                          style={{ fontSize: 11, padding: "2px 8px", borderRadius: 6, border: "1px solid #e1e4e8", background: "#fff", color: "#0078d4", cursor: "pointer" }}
                        >
                          Alle auswählen
                        </button>
                        <button
                          onClick={() => setSelectedDeactivate(new Set())}
                          disabled={selectedDeactivate.size === 0}
                          style={{ fontSize: 11, padding: "2px 8px", borderRadius: 6, border: "1px solid #e1e4e8", background: "#fff", color: "#666", cursor: "pointer" }}
                        >
                          Auswahl aufheben
                        </button>
                      </div>
                      <div style={{ maxHeight: 300, overflow: "auto", border: "1px solid #e1e4e8", borderRadius: 8 }}>
                        {preview.toDeactivate.map((e) => (
                          <label key={e.hrlab_id} style={{
                            display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                            borderBottom: "1px solid #f0f0f0", cursor: "pointer", fontSize: 13,
                          }}>
                            <input
                              type="checkbox"
                              checked={selectedDeactivate.has(e.hrlab_id)}
                              onChange={() => toggleSelected(selectedDeactivate, setSelectedDeactivate, e.hrlab_id)}
                            />
                            <span style={{ fontWeight: 500 }}>{e.name}</span>
                            <span style={{ color: "#888" }}>{e.email}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <button
                      onClick={handleExecute}
                      disabled={executing || (selectedCreate.size === 0 && selectedDeactivate.size === 0)}
                      style={{
                        padding: "11px 24px", fontSize: 14, fontWeight: 600, borderRadius: 8,
                        background: (selectedCreate.size === 0 && selectedDeactivate.size === 0) ? "#ccc" : "#d13438",
                        color: "white", border: "none", cursor: "pointer",
                      }}
                    >
                      {executing ? "⏳ Wird ausgeführt..." : "✅ Ausgewählte Aktionen ausführen"}
                    </button>
                    <span style={{ fontSize: 12, color: "#999" }}>
                      ⚠️ Nicht rückgängig zu machen
                    </span>
                  </div>
                  {executing && <ProgressBar label="Aktionen werden in Azure AD ausgeführt…" />}
                </div>
              )}

              {preview.excluded?.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, margin: "0 0 8px", color: "#888" }}>
                    🙈 Ignoriert – kein AD-Konto nötig ({preview.excluded.length})
                  </h3>
                  <div style={{ maxHeight: 200, overflow: "auto", border: "1px solid #e1e4e8", borderRadius: 8 }}>
                    {preview.excluded.map((e) => (
                      <Link key={e.hrlab_id} to={`/employees/${e.hrlab_id}`} style={{
                        display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                        borderBottom: "1px solid #f0f0f0", fontSize: 13, textDecoration: "none", color: "inherit",
                      }}>
                        <span style={{ fontWeight: 500 }}>{e.name}</span>
                        <span style={{ color: "#888" }}>{e.email}</span>
                        {e.reason && <span style={{ color: "#aaa", fontStyle: "italic" }}>– {e.reason}</span>}
                      </Link>
                    ))}
                  </div>
                  <p style={{ fontSize: 12, color: "#999", marginTop: 6 }}>
                    Über die Mitarbeiter-Detailseite kann diese Markierung wieder entfernt werden.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Schritt 3: Mitarbeiter-Kontaktverteilung */}
        <div className="card">
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div style={{
              background: "#10781010", border: "2px solid #107c10", borderRadius: 8,
              width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 700, color: "#107c10", fontSize: 16, flexShrink: 0,
            }}>3</div>
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Mitarbeiter-Kontakte verteilen</h2>
              <p style={{ fontSize: 13, color: "#666", margin: 0 }}>
                Legt für jeden aktiven Mitarbeiter (mit aktivem AD-Konto) im persönlichen Outlook-Kontaktordner
                "Mitarbeiterverzeichnis" einen Kontakt für jeden anderen aktiven Mitarbeiter an, aktualisiert
                geänderte Kontakte und entfernt Kontakte ausgeschiedener Mitarbeiter. Läuft als eigener Schritt
                mit Vorschau – wird nicht automatisch von Schritt 2 mit ausgeführt.
              </p>
            </div>
          </div>

          {contactsPreviewError && (
            <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 14, background: "#fde7e9", color: "#d13438", fontSize: 14 }}>
              ❌ {contactsPreviewError}
            </div>
          )}

          {contactsExecuteResult && (
            <div style={{
              padding: "10px 14px", borderRadius: 8, marginBottom: 14,
              background: contactsExecuteResult.type === "success" ? "#dff6dd" : "#fde7e9",
              color: contactsExecuteResult.type === "success" ? "#107c10" : "#d13438",
              fontSize: 14,
            }}>
              {contactsExecuteResult.type === "success" ? (
                <>✅ Kontaktverteilung gestartet – Fortschritt siehe <Link to="/logs" style={{ color: "#107c10", fontWeight: 600 }}>Logs</Link>.</>
              ) : (
                <>❌ {contactsExecuteResult.message}</>
              )}
            </div>
          )}

          <button
            className="btn-secondary"
            onClick={handleLoadContactsPreview}
            disabled={contactsPreviewLoading}
            style={{ marginBottom: 16 }}
          >
            {contactsPreviewLoading ? "⏳ Lädt Vorschau..." : "🔍 Kontakte-Vorschau laden"}
          </button>

          {contactsPreviewLoading && <ProgressBar label="Kontakte-Vorschau wird geladen…" />}

          {contactsPreview && (
            <>
              {contactsPreview.inSync ? (
                <div style={{ padding: "12px 16px", borderRadius: 8, background: "#dff6dd", color: "#107c10", fontSize: 14, fontWeight: 600 }}>
                  ✅ Alle Kontakte sind synchron – keine Aktionen nötig.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <p style={{ fontSize: 13, color: "#666", margin: 0 }}>
                    {contactsPreview.scope_count} Mitarbeiter im Geltungsbereich · insgesamt{" "}
                    <strong>➕ {contactsPreview.totals.create} anlegen</strong>,{" "}
                    <strong>✏️ {contactsPreview.totals.update} aktualisieren</strong>,{" "}
                    <strong>🗑️ {contactsPreview.totals.delete} entfernen</strong>{" "}
                    ({contactsPreview.totals.unchanged} unverändert)
                  </p>

                  <div style={{ maxHeight: 400, overflow: "auto", border: "1px solid #e1e4e8", borderRadius: 8 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: "#f8f9fa", position: "sticky", top: 0 }}>
                          {["Mitarbeiter (Postfach)", "➕ Neu", "✏️ Geändert", "🗑️ Entfernen", "Unverändert"].map((h) => (
                            <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#444" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {contactsPreview.owners
                          .filter((o) => o.toCreate || o.toUpdate || o.toDelete)
                          .map((o) => (
                            <tr key={o.hrlab_id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                              <td style={{ padding: "6px 12px" }}>
                                {o.name} <span style={{ color: "#888" }}>{o.email}</span>
                              </td>
                              <td style={{ padding: "6px 12px" }}>{o.toCreate || "—"}</td>
                              <td style={{ padding: "6px 12px" }}>{o.toUpdate || "—"}</td>
                              <td style={{ padding: "6px 12px" }}>{o.toDelete || "—"}</td>
                              <td style={{ padding: "6px 12px", color: "#999" }}>{o.unchanged}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <button
                      onClick={handleExecuteContacts}
                      disabled={contactsExecuting}
                      style={{
                        padding: "11px 24px", fontSize: 14, fontWeight: 600, borderRadius: 8,
                        background: "#107c10", color: "white", border: "none", cursor: "pointer",
                      }}
                    >
                      {contactsExecuting ? "⏳ Wird gestartet..." : "✅ Kontaktverteilung ausführen"}
                    </button>
                    <span style={{ fontSize: 12, color: "#999" }}>
                      ⚠️ Läuft im Hintergrund, Fortschritt siehe Logs
                    </span>
                  </div>
                  {contactsExecuting && <ProgressBar label="Kontaktverteilung wird gestartet…" />}
                </div>
              )}
            </>
          )}
        </div>

        {/* WP Office Sync (Plugin-Fernsteuerung, siehe README_Adapter.md) */}
        <WpOfficeSyncCard />

        {/* Diagnose: AD-Nutzer direkt abrufen */}
        <div className="card">
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div style={{
              background: "#60606010", border: "2px solid #888", borderRadius: 8,
              width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 700, color: "#888", fontSize: 16, flexShrink: 0,
            }}>?</div>
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Diagnose</h2>
              <p style={{ fontSize: 13, color: "#666", margin: 0 }}>
                Alle Azure-AD-Nutzer direkt abrufen oder prüfen, warum ein Mitarbeiter nicht zugeordnet wird.
              </p>
            </div>
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
            <button className="btn-secondary" onClick={handleLoadADUsers} disabled={adUsersLoading}>
              {adUsersLoading ? "⏳ Lädt..." : "👥 Alle AD-Nutzer laden"}
            </button>
            <button className="btn-secondary" onClick={handleDebugMatch} disabled={matchLoading}>
              {matchLoading ? "⏳ Lädt..." : "🔍 Matching-Diagnose (HRLab ↔ AD)"}
            </button>
          </div>

          {(adUsersLoading || matchLoading) && (
            <ProgressBar label={adUsersLoading ? "AD-Nutzer werden geladen…" : "Matching-Diagnose läuft…"} />
          )}

          {adUsersError && (
            <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 14, background: "#fde7e9", color: "#d13438", fontSize: 14 }}>
              ❌ {adUsersError}
            </div>
          )}

          {adUsers && (
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 13, color: "#666", marginBottom: 8 }}>
                {adUsers.total} Nutzer in Azure AD gefunden:
              </p>
              <div style={{ maxHeight: 400, overflow: "auto", border: "1px solid #e1e4e8", borderRadius: 8 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#f8f9fa", position: "sticky", top: 0 }}>
                      {["Name", "Mail", "UPN", "Aktiv"].map((h) => (
                        <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#444" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {adUsers.users.map((u) => (
                      <tr key={u.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "6px 12px" }}>{u.displayName}</td>
                        <td style={{ padding: "6px 12px", color: "#555" }}>{u.mail || "—"}</td>
                        <td style={{ padding: "6px 12px", color: "#555" }}>{u.userPrincipalName || "—"}</td>
                        <td style={{ padding: "6px 12px" }}>{u.accountEnabled ? "✅" : "🚫"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {matchError && (
            <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 14, background: "#fde7e9", color: "#d13438", fontSize: 14 }}>
              ❌ {matchError}
            </div>
          )}

          {matchResult && (
            <div>
              <p style={{ fontSize: 13, color: "#666", marginBottom: 8 }}>
                {matchResult.hrlab_active_total} aktive HRLab-Mitarbeiter, {matchResult.ad_users_total} AD-Nutzer —
                {" "}<strong>{matchResult.matched} zugeordnet, {matchResult.unmatched_count} ohne Match</strong>
              </p>
              {matchResult.unmatched_count > 0 && (
                <div style={{ maxHeight: 400, overflow: "auto", border: "1px solid #e1e4e8", borderRadius: 8 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: "#f8f9fa", position: "sticky", top: 0 }}>
                        {["Name", "Geprüfte E-Mails (HRLab)"].map((h) => (
                          <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#444" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {matchResult.unmatched.map((r) => (
                        <tr key={r.hrlab_id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                          <td style={{ padding: "6px 12px" }}>{r.name}</td>
                          <td style={{ padding: "6px 12px", color: "#555" }}>
                            {r.email_candidates.length ? r.email_candidates.join(", ") : "(keine E-Mail)"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
